import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, CheckCircle } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { publicApi } from "@/lib/api";
import { toast } from "sonner";

const WriteReview = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ customer_name: "", email: "", rating: 5, review: "", package_name: "" });
  const [packages, setPackages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    publicApi.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.review.length < 10) {
      toast.error("Please write at least 10 characters");
      return;
    }
    setSubmitting(true);
    try {
      await publicApi.post("/testimonials/submit", form);
      setDone(true);
      toast.success("Thank you! Your review is under review.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="write-review-page" className="grain-texture min-h-screen">
      <SEO title="Write a Review" description="Share your Andaman Avalon experience — write a review and help future travellers." path="/write-review" />
      <Navbar />

      <section className="pt-32 pb-8 bg-primary text-primary-foreground">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="accent-label text-secondary">SHARE YOUR EXPERIENCE</span>
          <h1 className="text-4xl sm:text-5xl font-serif mt-3 text-white">Write a Review</h1>
          <p className="text-white/80 mt-3">Your review helps future travellers pick the right package for their Andaman journey.</p>
        </div>
      </section>

      <section className="py-16 bg-background">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {done ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center admin-card" data-testid="review-success">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="text-green-600" size={32} />
              </div>
              <h2 className="text-2xl font-serif text-primary mb-3">Thank you!</h2>
              <p className="text-muted-foreground mb-6">Your review has been submitted and is now awaiting approval. Once approved, it will appear on our homepage.</p>
              <Button data-testid="review-back-home" onClick={() => navigate("/home")} className="bg-primary hover:bg-primary/90">Back to Home</Button>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="review-form" className="admin-card space-y-5">
              <div>
                <label className="block text-sm font-medium mb-2">Your Name *</label>
                <Input
                  data-testid="review-name"
                  value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                  required
                  placeholder="How should we display your name?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Email (optional, kept private)</label>
                <Input
                  data-testid="review-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Which package did you travel with? *</label>
                <select
                  data-testid="review-package"
                  value={form.package_name}
                  onChange={(e) => setForm({ ...form, package_name: e.target.value })}
                  required
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                >
                  <option value="">Select a package…</option>
                  {packages.map((p) => (
                    <option key={p.id} value={p.name}>{p.name}</option>
                  ))}
                  <option value="Custom Andaman trip">Custom Andaman trip</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Rating *</label>
                <div className="flex items-center gap-1" data-testid="review-rating">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setForm({ ...form, rating: n })}
                      data-testid={`review-star-${n}`}
                      className="p-1 transition-transform hover:scale-110"
                      aria-label={`${n} stars`}
                    >
                      <Star size={32} className={n <= form.rating ? "fill-secondary text-secondary" : "text-muted-foreground/40"} />
                    </button>
                  ))}
                  <span className="ml-3 text-sm text-muted-foreground">{form.rating}/5</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Your Review *</label>
                <Textarea
                  data-testid="review-text"
                  value={form.review}
                  onChange={(e) => setForm({ ...form, review: e.target.value })}
                  required
                  minLength={10}
                  rows={5}
                  placeholder="Tell others what you loved about your Andaman experience with us…"
                />
                <p className="text-xs text-muted-foreground mt-1">Minimum 10 characters. {form.review.length} typed.</p>
              </div>

              <Button
                type="submit"
                data-testid="review-submit-btn"
                disabled={submitting}
                className="w-full bg-primary hover:bg-primary/90 h-11"
              >
                {submitting ? "Submitting…" : "Submit Review"}
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                Reviews are moderated before appearing on the site to prevent spam.
              </p>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WriteReview;
